/**
 * Numeric versionCode comparison for the Android update prompt.
 *
 * Play's In-App Updates API often reports UPDATE_NOT_AVAILABLE on internal
 * testing tracks even when a newer AAB is published. We then compare the
 * running versionCode to a published store version (remote JSON / env).
 * Comparison MUST be numeric — string compare ranks "9" above "88".
 */

/** Play applicationId. Keep in sync with `frontend/capacitor.config.ts` `appId`. */
export const ANDROID_PLAY_PACKAGE_ID = 'com.ascendhealth.comms'

/** HTTPS listing used when the native `market://` intent cannot run. */
export const ANDROID_PLAY_STORE_HTTPS_URL =
  `https://play.google.com/store/apps/details?id=${ANDROID_PLAY_PACKAGE_ID}`

/** Play Store app deep link. Prefer this over a Custom Tabs / WebView open. */
export const ANDROID_PLAY_STORE_MARKET_URL = `market://details?id=${ANDROID_PLAY_PACKAGE_ID}`

export function parseAndroidVersionCode(raw: unknown): number | null {
  if (raw == null || raw === '') return null
  const text = String(raw).trim()
  // Marketing versionName ("1.0.0") is not a versionCode. Number("1.0") is 1,
  // which would look ancient next to 136 and loop testers into the store.
  if (text.includes('.')) return null
  const n = typeof raw === 'number' ? raw : Number(text)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.floor(n)
}

export function isRunningBehindStoreVersion(
  currentVersionCode: unknown,
  storeVersionCode: number,
): boolean {
  const current = parseAndroidVersionCode(currentVersionCode)
  if (current == null) return false
  return current < storeVersionCode
}

/**
 * True only when both sides parse as a numeric versionCode and current < latest.
 * Equal and ahead must not prompt. Unparseable local (versionName, "newer") must not
 * prompt — we cannot tell they are behind.
 */
export function isAndroidUpdateNeeded(currentVersionCode: unknown, latestVersionCode: unknown): boolean {
  const latest = parseAndroidVersionCode(latestVersionCode)
  if (latest == null) return false
  return isRunningBehindStoreVersion(currentVersionCode, latest)
}

export function maxAndroidVersionCode(...values: Array<number | null | undefined>): number | null {
  let max: number | null = null
  for (const value of values) {
    if (value == null) continue
    if (max == null || value > max) max = value
  }
  return max
}
